import { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { colors, fontFamily, radii, space, type } from '~/ui/tokens';
import {
  addDaysIso,
  addMonths,
  formatRangeLabel,
  getMonthGrid,
  getMonthLabel,
  getWeekdayLabels,
  todayIso,
  type CalendarDay,
} from '../lib/dateUtils';
import { MAX_CHECKIN_DAYS_AHEAD, MAX_STAY_NIGHTS } from '../lib/api/constants';
import { Button } from './ui/Button';
import { IconButton } from './ui/IconButton';

export interface DateRange {
  start: string | null;
  end: string | null;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  onApply: () => void;
  minIso?: string;
  maxIso?: string;
  /** When set, the picker edits a single side of an existing range instead of
   * replacing it wholesale: picking a new check-out keeps check-in in place
   * (unless the new date falls before it, at which point it becomes the new
   * check-in), and symmetrically for check-in. Omit for the combined
   * "pick start then end" flow used by the single "Dates" field. */
  mode?: 'checkin' | 'checkout';
  /** Renders edge-to-edge to fill a full-screen mobile container instead of
   * the fixed-height card used inside a desktop popover. */
  fullScreen?: boolean;
  /** Footer button text; defaults to "Apply". */
  applyLabel?: string;
}

type DayState = 'disabled' | 'start' | 'end' | 'in-range' | 'default';

function getDayState(day: CalendarDay, value: DateRange, minIso: string, maxIso: string): DayState {
  if (!day.inCurrentMonth) return 'disabled';
  if (day.iso === value.start) return 'start';
  if (day.iso === value.end) return 'end';
  if (day.iso < minIso || day.iso > maxIso) return 'disabled';
  if (value.start && value.end && day.iso > value.start && day.iso < value.end) return 'in-range';
  return 'default';
}

export function ReferenceDateRangePicker({ value, onChange, onApply, minIso, maxIso, mode, fullScreen = false, applyLabel = 'Apply' }: DateRangePickerProps) {
  const resolvedMinIso = minIso ?? todayIso();
  const resolvedMaxIso = maxIso ?? addDaysIso(todayIso(), MAX_CHECKIN_DAYS_AHEAD);
  const { width, height } = useWindowDimensions();
  const stacked = width < 700;

  const now = new Date();
  // Open on the month already chosen, so editing a November stay doesn't start at today's month.
  const startMonth = value.start ? new Date(`${value.start}T00:00:00`) : now;
  const [view, setView] = useState({ year: startMonth.getFullYear(), month: startMonth.getMonth() });
  const secondView = addMonths(view.year, view.month, 1);
  // Mobile's full-screen calendar replaces the desktop popover's two-month,
  // click-through-arrows paging with a single continuous scroll a year deep —
  // there's room for it, and scrolling through months reads more naturally as
  // a full-screen gesture than clicking arrows one page at a time.
  const fullScreenMonths = fullScreen
    ? Array.from({ length: 12 }, (_, index) => addMonths(now.getFullYear(), now.getMonth(), index))
    : [];

  // While a check-out is being picked, days before check-in and past the 30-night
  // stay limit are greyed out; while a check-in is being picked against a known
  // check-out, days that would make the stay longer than 30 nights are.
  const pickingCheckout = Boolean(value.start) && (mode === 'checkout' || (!mode && !value.end));
  const dayMinIso = pickingCheckout && value.start
    ? (value.start > resolvedMinIso ? value.start : resolvedMinIso)
    : mode === 'checkin' && value.end
      ? (addDaysIso(value.end, -MAX_STAY_NIGHTS) > resolvedMinIso ? addDaysIso(value.end, -MAX_STAY_NIGHTS) : resolvedMinIso)
      : resolvedMinIso;
  const dayMaxIso = pickingCheckout && value.start
    ? (addDaysIso(value.start, MAX_STAY_NIGHTS) < resolvedMaxIso ? addDaysIso(value.start, MAX_STAY_NIGHTS) : resolvedMaxIso)
    : resolvedMaxIso;

  function selectDay(iso: string) {
    // Editing a single side of an already-complete range (the checkin/checkout
    // fields open this picker in that mode): the tapped date updates that side
    // only when it keeps the range valid, otherwise it becomes the new
    // check-in and check-out is cleared for re-selection — never silently
    // rewrites check-in just because check-out was the field tapped.
    if (mode === 'checkin') {
      if (value.end) {
        // An existing check-out is already in place — keep it fixed and only
        // move check-in around it, as long as the new date still precedes
        // it; otherwise that check-out is no longer valid and must be
        // re-picked (handled below, once this clears end to null).
        if (iso < value.end) {
          onChange({ start: iso, end: value.end });
        } else {
          onChange({ start: iso, end: null });
        }
      } else {
        // No check-out yet — the first tap sets check-in. A second tap here
        // (without leaving the check-in field) then completes the range as
        // check-out when it falls after check-in, or restarts check-in when
        // it doesn't. Comparing only against `value.start` (never `end`,
        // which is null in this branch) is what lets a second tap actually
        // land on check-out instead of just moving check-in again.
        if (value.start && iso > value.start) {
          onChange({ start: value.start, end: iso });
        } else {
          onChange({ start: iso, end: null });
        }
      }
      return;
    }
    if (mode === 'checkout') {
      // Tapping the highlighted check-out again turns it into the new check-in.
      if (value.end && iso === value.end) {
        onChange({ start: iso, end: null });
        return;
      }
      if (value.start && iso > value.start) {
        onChange({ start: value.start, end: iso });
      } else {
        onChange({ start: iso, end: null });
      }
      return;
    }

    const noRangeYet = !value.start || (value.start && value.end);
    if (noRangeYet) {
      onChange({ start: iso, end: null });
    } else if (iso <= value.start!) {
      onChange({ start: iso, end: null });
    } else {
      onChange({ start: value.start, end: iso });
    }
  }

  return (
    <View style={[styles.container, fullScreen && styles.containerFullScreen]}>
      <View style={styles.header}>
        <View style={styles.headerCopy}>
          <Text style={styles.title}>Choose your dates</Text>
          <Text style={styles.subtitle}>Select arrival and departure</Text>
        </View>
        {!fullScreen ? (
          <View style={styles.monthNavigation}>
            <IconButton icon="chevron-back" diameter={36} onPress={() => setView(addMonths(view.year, view.month, -1))} />
            <IconButton icon="chevron-forward" diameter={36} onPress={() => setView(addMonths(view.year, view.month, 1))} />
          </View>
        ) : null}
      </View>

      {/* The popover hosting this picker clips overflow instead of scrolling
          (see AnchoredPopover's portalPopover style), and two full month grids
          — stacked to one column on mobile — can be taller than the space
          below the anchor. Without its own scroll here, a swipe starting on a
          day cell had nothing to scroll, leaving Apply unreachable below the
          fold. Capped well under the viewport height so the header above and
          Apply below always stay visible outside the scrollable area. */}
      <ScrollView style={fullScreen ? styles.monthsScrollFull : { maxHeight: Math.min(480, height * 0.5) }}>
        {fullScreen ? (
          <View style={[styles.months, styles.monthsStacked]}>
            {fullScreenMonths.map(({ year, month }) => (
              <MonthGrid
                key={`${year}-${month}`}
                year={year}
                month={month}
                value={value}
                minIso={dayMinIso}
                maxIso={dayMaxIso}
                onSelectDay={selectDay}
              />
            ))}
          </View>
        ) : (
          <View style={[styles.months, stacked && styles.monthsStacked]}>
            <MonthGrid
              year={view.year}
              month={view.month}
              value={value}
              minIso={dayMinIso}
              maxIso={dayMaxIso}
              onSelectDay={selectDay}
            />
            <MonthGrid
              year={secondView.year}
              month={secondView.month}
              value={value}
              minIso={dayMinIso}
              maxIso={dayMaxIso}
              onSelectDay={selectDay}
            />
          </View>
        )}
      </ScrollView>

      <View style={styles.footer}>
        <Text style={styles.rangeLabel}>{formatRangeLabel(value.start, value.end)}</Text>
        <Button label={applyLabel} onPress={onApply} disabled={!value.start || !value.end} />
      </View>
    </View>
  );
}

interface MonthGridProps {
  year: number;
  month: number;
  value: DateRange;
  minIso: string;
  maxIso: string;
  onSelectDay: (iso: string) => void;
}

function MonthGrid({ year, month, value, minIso, maxIso, onSelectDay }: MonthGridProps) {
  const weeks = getMonthGrid(year, month);

  return (
    <View style={styles.grid}>
      <Text style={styles.monthLabel}>{getMonthLabel(year, month)}</Text>
      <View style={styles.weekRow}>
        {getWeekdayLabels().map((label) => (
          <Text key={label} style={styles.weekdayLabel}>
            {label}
          </Text>
        ))}
      </View>
      {weeks.map((week) => (
        <View key={week[0].iso} style={styles.weekRow}>
          {week.map((day) => {
            const state = getDayState(day, value, minIso, maxIso);
            return (
              <Pressable
                key={day.iso}
                accessibilityRole="button"
                accessibilityLabel={day.iso}
                accessibilityState={{ disabled: state === 'disabled', selected: state === 'start' || state === 'end' }}
                disabled={state === 'disabled'}
                onPress={() => onSelectDay(day.iso)}
                style={({ pressed }) => [
                  styles.dayCell,
                  state === 'in-range' && styles.dayCellInRange,
                  pressed && state === 'default' && styles.dayCellPressed,
                ]}
              >
                <View style={[(state === 'start' || state === 'end') && styles.dayDot]}>
                  <Text
                    style={[
                      styles.dayLabel,
                      state === 'disabled' && styles.dayLabelDisabled,
                      (state === 'start' || state === 'end') && styles.dayLabelEdge,
                    ]}
                  >
                    {day.day}
                  </Text>
                </View>
              </Pressable>
            );
          })}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.card,
    borderRadius: radii.lg,
    padding: space.card,
    gap: space.card,
  },
  containerFullScreen: {
    flex: 1,
    borderRadius: radii.lg,
  },
  monthsScrollFull: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: space.compact,
    borderBottomWidth: 1,
    borderBottomColor: colors.lineSoft,
  },
  headerCopy: {
    gap: space.related,
  },
  title: {
    fontFamily: fontFamily.semibold,
    ...type.sectionTitle,
    color: colors.ink,
  },
  subtitle: {
    fontFamily: fontFamily.regular,
    ...type.metadata,
    color: colors.inkFaint,
  },
  monthNavigation: {
    flexDirection: 'row',
    gap: space.related,
  },
  monthLabel: {
    fontFamily: fontFamily.semibold,
    fontSize: type.cardTitle.fontSize,
    color: colors.ink,
    paddingHorizontal: space.micro,
    marginBottom: space.related,
  },
  months: {
    flexDirection: 'row',
    gap: space.cardLarge,
    width: '100%',
  },
  monthsStacked: {
    flexDirection: 'column',
  },
  grid: {
    flex: 1,
    gap: space.related,
    minWidth: 0,
  },
  weekRow: {
    flexDirection: 'row',
  },
  weekdayLabel: {
    flex: 1,
    textAlign: 'center',
    fontFamily: fontFamily.medium,
    fontSize: type.metadata.fontSize,
    color: colors.inkFaint,
    paddingBottom: space.related,
  },
  dayCell: {
    flex: 1,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayCellInRange: {
    backgroundColor: colors.skyTint,
  },
  dayCellPressed: {
    backgroundColor: colors.lineSoft,
  },
  dayDot: {
    width: 38,
    height: 38,
    borderRadius: radii.full,
    backgroundColor: colors.brand,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayLabel: {
    fontFamily: fontFamily.regular,
    fontSize: type.bodyRegular.fontSize,
    color: colors.ink,
  },
  dayLabelDisabled: {
    color: colors.line,
  },
  dayLabelEdge: {
    fontFamily: fontFamily.semibold,
    color: colors.white,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: space.internal,
    borderTopWidth: 1,
    borderTopColor: colors.lineSoft,
  },
  rangeLabel: {
    fontFamily: fontFamily.semibold,
    fontSize: type.bodyLarge.fontSize,
    color: colors.ink,
  },
});
